/*
 * Clubhouse Quiz Engine
 * ----------------------
 * Vanilla JS, no build step, no framework. Loads quiz-config.json,
 * walks the user through the questions, and renders the results page
 * with personalized recommendations based on the rules in the config.
 *
 * Designed to be dropped into a Shopify theme as assets/quiz-app.js
 * and instantiated by the section template (assets/quiz-app.liquid).
 */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    var root = document.querySelector('[data-ch-quiz]');
    if (!root) return;
    var configUrl = root.dataset.configUrl;
    var storageKey = root.dataset.storageKey || 'ch-quiz-state-v1';

    fetch(configUrl, { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (config) { initQuiz(root, config, storageKey); })
      .catch(function (err) {
        console.error('[ClubhouseQuiz] Failed to load config', err);
        var stage = root.querySelector('[data-ch-stage]');
        if (stage) {
          stage.innerHTML = '<div class="ch-quiz__error">' +
            'Sorry — the quiz failed to load. Please refresh the page or ' +
            '<a href="/collections/clubhouse-formulas">browse our products</a>.' +
            '</div>';
        }
      });
  });

  function initQuiz(root, config, storageKey) {
    var stage = root.querySelector('[data-ch-stage]');
    var backBtn = root.querySelector('[data-ch-back]');
    var progressBar = root.querySelector('[data-ch-progress-bar]');
    var totalSteps = config.questions.length + 1; // +1 for email capture step

    // Quiz state — answered questions kept in sessionStorage so refresh doesn't lose progress
    var state = loadState();

    backBtn.addEventListener('click', function () {
      if (state.stepIndex > 0) {
        state.stepIndex--;
        saveState();
        renderCurrent();
      }
    });

    renderCurrent();

    // ---------------- State helpers ----------------

    function loadState() {
      try {
        var raw = sessionStorage.getItem(storageKey);
        if (raw) return JSON.parse(raw);
      } catch (e) { /* ignore */ }
      return { stepIndex: 0, answers: {}, email: null, emailSubmitted: false };
    }

    function saveState() {
      try { sessionStorage.setItem(storageKey, JSON.stringify(state)); } catch (e) { /* ignore */ }
    }

    function resetState() {
      state = { stepIndex: 0, answers: {}, email: null, emailSubmitted: false };
      try { sessionStorage.removeItem(storageKey); } catch (e) { /* ignore */ }
    }

    // ---------------- Render dispatcher ----------------

    function renderCurrent() {
      // Update progress + back button
      var stepNum = state.stepIndex + 1;
      var pct = Math.min(100, Math.round((stepNum / totalSteps) * 100));
      progressBar.style.width = pct + '%';
      backBtn.hidden = state.stepIndex === 0;

      // Out of bounds = render results
      if (state.stepIndex >= config.questions.length + 1) {
        renderResults();
        return;
      }

      // Email capture step comes after the last question
      if (state.stepIndex === config.questions.length) {
        renderEmailCapture();
        return;
      }

      renderQuestion(config.questions[state.stepIndex]);
    }

    // ---------------- Question rendering ----------------

    function renderQuestion(q) {
      var existing = state.answers[q.id];
      var html = '';
      html += '<div class="ch-quiz__question">';
      html += '  <h2 class="ch-quiz__title">' + escapeHtml(q.title) + '</h2>';
      if (q.subtitle) {
        html += '  <p class="ch-quiz__subtitle">' + escapeHtml(q.subtitle) + '</p>';
      }
      html += '  <div class="ch-quiz__options ch-quiz__options--' + (q.type === 'multi' ? 'multi' : 'single') + '" role="' + (q.type === 'multi' ? 'group' : 'radiogroup') + '">';
      q.options.forEach(function (opt) {
        var isSelected = q.type === 'multi'
          ? Array.isArray(existing) && existing.indexOf(opt.value) !== -1
          : existing === opt.value;
        html += '    <button type="button" class="ch-quiz__option' + (isSelected ? ' is-selected' : '') + '" data-opt-id="' + escapeAttr(opt.id) + '" data-opt-value="' + escapeAttr(opt.value) + '">';
        if (opt.icon) {
          html += '<span class="ch-quiz__option-icon ch-quiz__option-icon--' + escapeAttr(opt.icon) + '" aria-hidden="true"></span>';
        }
        html += '<span class="ch-quiz__option-label">' + escapeHtml(opt.label) + '</span>';
        html += '    </button>';
      });
      html += '  </div>';
      if (q.type === 'multi') {
        var canProceed = Array.isArray(existing) && existing.length >= (q.min_selections || 1);
        html += '  <button type="button" class="ch-quiz__next" data-ch-next' + (canProceed ? '' : ' disabled') + '>NEXT</button>';
      }
      html += '</div>';
      stage.innerHTML = html;

      // Wire up clicks
      var optionEls = stage.querySelectorAll('.ch-quiz__option');
      optionEls.forEach(function (el) {
        el.addEventListener('click', function () {
          var val = el.dataset.optValue;
          if (q.type === 'multi') {
            var current = Array.isArray(state.answers[q.id]) ? state.answers[q.id].slice() : [];
            var idx = current.indexOf(val);
            if (idx === -1) {
              if (current.length < (q.max_selections || 99)) current.push(val);
            } else {
              current.splice(idx, 1);
            }
            state.answers[q.id] = current;
            saveState();
            // Re-render to update selected states
            renderCurrent();
          } else {
            state.answers[q.id] = val;
            saveState();
            // Auto-advance for single-select
            advance();
          }
        });
      });

      var nextBtn = stage.querySelector('[data-ch-next]');
      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          if (nextBtn.hasAttribute('disabled')) return;
          advance();
        });
      }
    }

    function advance() {
      state.stepIndex++;
      saveState();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      renderCurrent();
    }

    // ---------------- Email capture ----------------

    function renderEmailCapture() {
      var ec = config.email_capture;
      var html = '';
      html += '<div class="ch-quiz__email">';
      html += '  <h2 class="ch-quiz__title">' + escapeHtml(ec.heading) + '</h2>';
      html += '  <p class="ch-quiz__subtitle">' + escapeHtml(ec.subheading) + '</p>';
      html += '  <form class="ch-quiz__email-form" data-ch-email-form novalidate>';
      html += '    <input type="email" class="ch-quiz__email-input" name="email" placeholder="Email Address" autocomplete="email" required>';
      html += '    <button type="submit" class="ch-quiz__email-submit">' + escapeHtml(ec.submit_label) + '</button>';
      html += '  </form>';
      html += '  <p class="ch-quiz__email-consent">By entering your email you agree to receive marketing emails from Clubhouse Supplements. You can unsubscribe at any time. See our <a href="/policies/privacy-policy">Privacy Policy</a>.</p>';
      html += '  <div class="ch-quiz__email-or">Or</div>';
      html += '  <button type="button" class="ch-quiz__email-skip" data-ch-email-skip>' + escapeHtml(ec.skip_label) + '</button>';
      html += '</div>';
      stage.innerHTML = html;

      stage.querySelector('[data-ch-email-form]').addEventListener('submit', function (e) {
        e.preventDefault();
        var input = e.target.querySelector('input[name="email"]');
        var value = (input.value || '').trim();
        if (!value || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
          input.focus();
          input.classList.add('is-invalid');
          return;
        }
        state.email = value;
        state.emailSubmitted = true;
        saveState();
        sendEmailToOmnisend(value);
        advance();
      });

      stage.querySelector('[data-ch-email-skip]').addEventListener('click', function () {
        state.emailSubmitted = false;
        saveState();
        advance();
      });
    }

function sendEmailToOmnisend(email) {
      // Send to our serverless proxy, which adds the API key + tag server-side.
      // keepalive lets the request finish even as the UI advances to results.
      try {
        fetch('https://sparkling-unit-c3d8.skkaushik212.workers.dev/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: email,
            properties: collectAnswersForCrm(state.answers)
          }),
          keepalive: true
        }).catch(function () { /* never block the quiz on a network error */ });
      } catch (e) {
        console.warn('[ClubhouseQuiz] Omnisend send failed', e);
      }
    }

    function collectAnswersForCrm(answers) {
      // Flatten quiz answers into a flat object suitable for Omnisend custom properties.
      var out = {};
      Object.keys(answers).forEach(function (qid) {
        var v = answers[qid];
        out['quiz_' + qid] = Array.isArray(v) ? v.join(' | ') : v;
      });
      return out;
    }

    // ---------------- Recommendation engine ----------------

    function pickRecommendation() {
      var q1 = state.answers.q1;
      var q3 = state.answers.q3 || [];
      var q5 = state.answers.q5;
      var q7 = state.answers.q7;

      // When the user picks exactly 2 Q3 options, use the detailed rules.
      if (Array.isArray(q3) && q3.length === 2) {
        var sortedQ3 = q3.slice().sort();
        var match = config.rules_detailed.find(function (r) {
          return r.q1 === q1 && r.q5 === q5 && r.q7 === q7 &&
                 r.q3_any_two_of[0] === sortedQ3[0] && r.q3_any_two_of[1] === sortedQ3[1];
        });
        if (match) return { products: match.recommendation, source: 'detailed' };
      }

      // Fallback to Q1+Q5+Q7 rules for any other Q3 count (0, 1, 3, 4, or 5 selections).
      var fb = config.rules_fallback.find(function (r) {
        return r.q1 === q1 && r.q5 === q5 && r.q7 === q7;
      });
      if (fb) return { products: fb.recommendation, source: 'fallback' };

      // Last-resort default: the hero product for the user's Q1 problem.
      var hero = config.hero_product_by_q1[q1];
      return { products: hero ? [hero] : ['wood'], source: 'hero-default' };
    }

    // "All-In Stack" is a virtual recommendation meaning "every formula". The store
    // doesn't sell it as one bundle, so instead of a single dead-linked card we show
    // every individual formula, each linking to its own product page.
    function expandAllInStack(products) {
      if (!products || products.indexOf('all-in-stack') === -1) return products;
      var components = config.all_in_stack_components ||
        Object.keys(config.products).filter(function (k) { return k !== 'all-in-stack'; });
      // Start from all components, then append any other explicitly-recommended real
      // products that somehow aren't already in the component list (defensive).
      var out = components.slice();
      products.forEach(function (p) {
        if (p !== 'all-in-stack' && out.indexOf(p) === -1) out.push(p);
      });
      return out;
    }

    function buildHeadline() {
      var q1 = state.answers.q1;
      var q5 = state.answers.q5;
      if (q5 === 'Honestly — all of the above!!!!') {
        return config.result_headline_all || "BASED ON YOUR ANSWERS, HERE'S WHAT WE RECOMMEND:";
      }
      var struggling = config.struggling_phrase_by_q1[q1] || q1;
      var outcome = q5 || '';
      return config.result_headline_template
        .replace('{struggling}', struggling)
        .replace('{outcome}', outcome.toUpperCase());
    }

    function pickDiscountCode() {
      return state.emailSubmitted
        ? config.discount_codes.email_submitted
        : config.discount_codes.email_skipped;
    }

    // ---------------- Results page ----------------

    function renderResults() {
      var rec = pickRecommendation();
      // "All-In Stack" isn't a single buyable product — expand it into every real
      // Clubhouse formula so each gets its own card and its own working product link.
      var products = expandAllInStack(rec.products);
      var hero = config.hero_product_by_q1[state.answers.q1] || products[0];
      // Hero product is shown first; remaining products are the "boost your results" stack.
      var heroKey = products.indexOf(hero) !== -1 ? hero : products[0];
      var stack = products.filter(function (p) { return p !== heroKey; });
      var discount = pickDiscountCode();
      var headline = buildHeadline();

      var html = '';
      html += '<div class="ch-quiz__results">';
      html += '  <h2 class="ch-quiz__results-headline">' + headline + '</h2>';
      html += '  <div class="ch-quiz__discount">Use code <strong>' + escapeHtml(discount) + '</strong> at checkout</div>';
      html += '  <p class="ch-quiz__results-intro">' + escapeHtml(config.results_page.intro_label) + '</p>';
      html += renderProductCard(heroKey, true);
      if (stack.length) {
        html += '  <p class="ch-quiz__stack-label">' + escapeHtml(config.results_page.stack_label) + '</p>';
        stack.forEach(function (key) {
          html += renderProductCard(key, false);
        });
      }
      html += '  <button type="button" class="ch-quiz__retake" data-ch-retake>' + escapeHtml(config.results_page.retake_label) + '</button>';
      html += '</div>';
      stage.innerHTML = html;
      backBtn.hidden = true;
      progressBar.style.width = '100%';

      stage.querySelector('[data-ch-retake]').addEventListener('click', function () {
        resetState();
        window.scrollTo({ top: 0, behavior: 'smooth' });
        renderCurrent();
      });

      // Fire a custom event so GA4/GTM/Shopify pixels can record the result if desired
      try {
        document.dispatchEvent(new CustomEvent('clubhouse:quiz_completed', {
          detail: {
            answers: state.answers,
            recommendation: products,
            hero: heroKey,
            discount_code: discount,
            email_submitted: state.emailSubmitted,
            email: state.emailSubmitted ? state.email : null
          }
        }));
        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'quiz_completed',
            quiz_hero: heroKey,
            quiz_stack: products.join('+'),
            quiz_discount: discount,
            quiz_email_submitted: state.emailSubmitted
          });
        }
      } catch (e) { /* ignore */ }
    }

    function renderProductCard(key, isHero) {
      var p = config.products[key];
      if (!p) return '<div class="ch-quiz__card ch-quiz__card--missing">Product config missing: ' + escapeHtml(key) + '</div>';
      var html = '';
      html += '<article class="ch-quiz__card' + (isHero ? ' ch-quiz__card--hero' : ' ch-quiz__card--stack') + '">';
      html += '  <div class="ch-quiz__card-image">';
      html += '    <img src="' + escapeAttr(p.image) + '" alt="' + escapeAttr(p.image_alt) + '" loading="lazy">';
      html += '  </div>';
      html += '  <div class="ch-quiz__card-body">';
      html += '    <h3 class="ch-quiz__card-title">' + escapeHtml(p.name) + '</h3>';
      html += '    <ul class="ch-quiz__card-benefits">';
      p.benefits.forEach(function (b) {
        html += '<li>' + escapeHtml(b) + '</li>';
      });
      html += '    </ul>';
      html += '    <p class="ch-quiz__card-tagline">' + escapeHtml(p.tagline) + '</p>';
      html += '    <a class="ch-quiz__card-cta" href="/products/' + escapeAttr(p.handle) + '">' + escapeHtml(config.results_page.view_details_label) + '</a>';
      html += '  </div>';
      html += '</article>';
      return html;
    }

    // ---------------- Utilities ----------------

    function escapeHtml(s) {
      if (s == null) return '';
      return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
    function escapeAttr(s) {
      return escapeHtml(s);
    }
  }
})();
